import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
} from "@mui/material";
import { BLUE_LOADING_BUTTON } from "portal/theme/theme";
import { buildPermission } from "portal/utils/auth";
import { CustomerSelector } from "../customers/CustomerSelector";
import { EditableTextRow } from "portal/components/EditableTextRow";
import { isUndefined } from "portal/utils/identity";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";
import { portalApi } from "portal/state/portalApi";
import { RobotSummaryResponse } from "protos/portal/robots";
import { Trans, useTranslation } from "react-i18next";
import {
  useAuthorizationRequired,
  withAuthorizationRequired,
} from "../auth/WithAuthorizationRequired";
import {
  useMutationPopups,
  useQueryPopups,
} from "portal/utils/hooks/useApiPopups";
import React, { FunctionComponent, useEffect, useState } from "react";
import SaveIcon from "@mui/icons-material/SaveOutlined";

const _RobotCustomerSelector: FunctionComponent<{
  summary?: RobotSummaryResponse;
  readOnly?: boolean;
}> = ({ summary, readOnly = false }) => {
  const { t } = useTranslation();

  const [customerId, setCustomerId] = useState<number | undefined>();
  useEffect(() => {
    setCustomerId(summary?.customer?.db?.id);
  }, [summary?.customer]);

  const { data: customers } = useQueryPopups(
    portalApi.endpoints.listCustomers.useQuery(undefined, {
      skip: readOnly,
    })
  );
  const customer = readOnly
    ? summary?.customer
    : customers?.find(
        (customer) => customer.db?.id === summary?.customer?.db?.id
      );

  const [isOpen, setOpen] = useState<boolean>(false);

  const [assignRobot, { isLoading: isAssignLoading }] = useMutationPopups(
    portalApi.endpoints.assignRobot.useMutation(),
    {
      success: t("utils.actions.updatedLong", {
        subject: t("models.configs.config_one"),
      }),
    }
  );

  const canUpdate =
    useAuthorizationRequired(
      buildPermission(
        PermissionAction.update,
        PermissionResource.robots,
        PermissionDomain.all
      )
    ) && !readOnly;

  return (
    <>
      <EditableTextRow
        text={customer?.name ?? t("components.Loading.placeholder")}
        onEdit={
          canUpdate
            ? () => {
                setCustomerId(summary?.customer?.db?.id);
                setOpen(true);
              }
            : undefined
        }
      />
      <Dialog
        open={isOpen}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t("components.customers.CustomerSelector.title")}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" className="mb-4">
            <Trans
              i18nKey="components.robots.RobotCustomerSelector.resaleWarning"
              t={t}
              components={{
                resaleLink: (
                  <Link
                    href="https://carbonrobotics.atlassian.net/wiki/spaces/Engineering/pages/1422524418/Robot+Resale"
                    target="_blank"
                    rel="noreferrer"
                  />
                ),
              }}
            />
          </Alert>
          <CustomerSelector
            value={customerId}
            onChange={(newId) => setCustomerId(newId)}
            small
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            disabled={isAssignLoading}
            onClick={() => setOpen(false)}
          >
            {t("utils.actions.cancel")}
          </Button>
          <Button
            {...BLUE_LOADING_BUTTON}
            loading={isAssignLoading}
            startIcon={<SaveIcon />}
            onClick={async () => {
              if (!summary || !summary.robot?.serial) {
                return;
              }
              if (!isUndefined(customerId)) {
                await assignRobot({
                  serial: summary.robot.serial,
                  customerId,
                });
                setOpen(false);
              }
            }}
          >
            {t("utils.actions.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const RobotCustomerSelector = withAuthorizationRequired(
  buildPermission(
    PermissionAction.read,
    PermissionResource.robots,
    PermissionDomain.all
  ),
  _RobotCustomerSelector
);
